import type { QueryClient, QueryKey } from '@tanstack/react-query';
import { reportApiCall } from '@/lib/client-telemetry';

export class ApiError extends Error {
  status: number;
  code: string | null;

  constructor(message: string, status: number, code: string | null = null) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }
}

const AUTH_INVALIDATED_EVENT = 'sales-suite:auth-invalidated';

function emitAuthInvalidated(): void {
  if (typeof window === 'undefined') {
    return;
  }
  window.dispatchEvent(new CustomEvent(AUTH_INVALIDATED_EVENT));
}

export function authInvalidatedEventName(): string {
  return AUTH_INVALIDATED_EVENT;
}

export async function fetchJson<T>(path: string, init?: RequestInit): Promise<T> {
  const startedAt = typeof performance !== 'undefined' ? performance.now() : 0;
  const method = (init?.method || 'GET').toUpperCase();
  const headers = new Headers(init?.headers);
  if (!headers.has('Content-Type')) headers.set('Content-Type', 'application/json');
  let response: Response;
  try {
    response = await fetch(path, { ...init, headers, credentials: "include" });
  } catch (error) {
    // Aborts are caller-driven (react-query cancellation), not failures.
    if (!(error instanceof DOMException && error.name === 'AbortError')) {
      reportApiCall({
        path,
        method,
        status: null,
        durationMs: performance.now() - startedAt,
        errorMessage: error instanceof Error ? error.message : String(error),
      });
    }
    throw error;
  }
  reportApiCall({ path, method, status: response.status, durationMs: performance.now() - startedAt });

  if (!response.ok) {
    const payload = await response.json().catch(() => ({}));
    // 502/503/504 come from the proxy while the backend restarts during a deploy.
    const fallbackMessage = response.status === 502 || response.status === 503 || response.status === 504
      ? 'Update in progress — check back in ~15 seconds.'
      : `Request failed: ${response.status}`;
    const message = payload.error || payload.message || fallbackMessage;
    const code = typeof payload.code === 'string' ? payload.code : null;
    if (response.status === 401) {
      emitAuthInvalidated();
    }
    throw new ApiError(message, response.status, code);
  }

  return (await response.json()) as T;
}

// Default read freshness; real-time reads (dialer resume, live calls) pass an explicit 0.
export const DEFAULT_QUERY_STALE_MS = 30_000;

export async function fetchCachedQuery<T>(
  queryClient: QueryClient,
  key: QueryKey,
  path: string,
  staleTime: number = DEFAULT_QUERY_STALE_MS
): Promise<T> {
  return queryClient.fetchQuery({
    queryKey: key,
    // Threading react-query's signal lets queryClient.cancelQueries(key) abort
    // the underlying fetch (used by heavy pages to cancel server work on
    // unmount). Inert for everything else.
    queryFn: ({ signal }) => fetchJson<T>(path, { signal }),
    staleTime,
  });
}

export type QueryLoadOptions = {
  fresh?: boolean;
};

export async function loadQuery<T>(
  queryClient: QueryClient,
  key: QueryKey,
  path: string,
  staleTime: number,
  options?: QueryLoadOptions
): Promise<T> {
  if (options?.fresh) {
    await queryClient.invalidateQueries({ queryKey: key, exact: true });
  }
  return fetchCachedQuery<T>(queryClient, key, path, options?.fresh ? 0 : staleTime);
}

export { buildQueryString } from '@/lib/api/keys';
